import { lazy, Suspense } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { Loader2, Lock } from 'lucide-react';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/toaster';
import { ConsentPreferencesLauncher } from '@/modules/consent/components/consent-preferences-launcher';
import { EmptyState } from '@/components/ui/empty-state';
import { AppLayout } from '@/layouts/app-layout';
import { AuthLayout } from '@/layouts/auth-layout';
import { LoginPage } from '@/modules/auth/pages/login';
import { LandingPage } from '@/modules/marketing/pages/home';
import { WhyAipaPage } from '@/modules/marketing/pages/why-aipa';
import { HelpCenterPage } from '@/modules/marketing/pages/help';
import { AboutPage } from '@/modules/marketing/pages/about';
import { ContactPage } from '@/modules/marketing/pages/contact';
import { BookADemoPage } from '@/modules/marketing/pages/book-a-demo';
import { IndustryDetailPage } from '@/modules/marketing/pages/industry-detail';
import { ProductDetailPage } from '@/modules/marketing/pages/product-detail';
import { PlatformDetailPage } from '@/modules/marketing/pages/platform-detail';
import { TermsPage } from '@/modules/marketing/pages/terms';
import { PrivacyPage } from '@/modules/marketing/pages/privacy';
import { useAuth, useAuthBootstrap } from '@/hooks/use-auth';
import { useNav } from '@/hooks/use-nav';
import { queryClient } from '@/lib/query-client';
import type { NavItem } from '@/types';

// Route-level code splitting: everything past login was one ~1MB bundle every
// visitor downloaded up front regardless of which module their plan even has
// enabled. Login stays a static import — it's the one page every visitor needs
// immediately, so lazy-loading it would just add a network round trip with no
// bundle-size benefit (nothing meaningful loads before it anyway).
const SuperAdminDashboardPage = lazy(() =>
    import('@/modules/super-admin/pages/dashboard').then((m) => ({ default: m.SuperAdminDashboardPage })),
);
const SuperAdminTenantsPage = lazy(() => import('@/modules/super-admin/pages/tenants').then((m) => ({ default: m.SuperAdminTenantsPage })));
const TenantModulesPage = lazy(() => import('@/modules/super-admin/pages/tenant-modules').then((m) => ({ default: m.TenantModulesPage })));
const IndustriesPage = lazy(() => import('@/modules/super-admin/pages/industries').then((m) => ({ default: m.IndustriesPage })));
const IndustryTemplatePage = lazy(() =>
    import('@/modules/super-admin/pages/industry-template').then((m) => ({ default: m.IndustryTemplatePage })),
);
const PlatformRolesPage = lazy(() => import('@/modules/super-admin/pages/platform-roles').then((m) => ({ default: m.PlatformRolesPage })));
const AuditLogsPage = lazy(() => import('@/modules/super-admin/pages/audit-logs').then((m) => ({ default: m.AuditLogsPage })));
const ReportsPage = lazy(() => import('@/modules/super-admin/pages/reports').then((m) => ({ default: m.ReportsPage })));
const PlatformAnalyticsPage = lazy(() =>
    import('@/modules/super-admin/pages/platform-analytics').then((m) => ({ default: m.PlatformAnalyticsPage })),
);
const SystemSettingsPage = lazy(() =>
    import('@/modules/super-admin/pages/system-settings').then((m) => ({ default: m.SystemSettingsPage })),
);
const DemoRequestsPage = lazy(() => import('@/modules/super-admin/pages/demo-requests').then((m) => ({ default: m.DemoRequestsPage })));
const HrEmployeesPage = lazy(() => import('@/modules/super-admin/pages/hr-employees').then((m) => ({ default: m.HrEmployeesPage })));
const ContactMessagesPage = lazy(() =>
    import('@/modules/super-admin/pages/contact-messages').then((m) => ({ default: m.ContactMessagesPage })),
);
const EditTenantPage = lazy(() => import('@/modules/super-admin/pages/edit-tenant').then((m) => ({ default: m.EditTenantPage })));
const NewTenantPage = lazy(() => import('@/modules/super-admin/pages/edit-tenant').then((m) => ({ default: m.NewTenantPage })));
const ModulesPage = lazy(() => import('@/modules/super-admin/pages/modules').then((m) => ({ default: m.ModulesPage })));
const PlatformUsersPage = lazy(() => import('@/modules/super-admin/pages/platform-users').then((m) => ({ default: m.PlatformUsersPage })));
const PlatformUserAddPage = lazy(() =>
    import('@/modules/super-admin/pages/platform-user-add').then((m) => ({ default: m.PlatformUserAddPage })),
);
const PlatformUserEditPage = lazy(() =>
    import('@/modules/super-admin/pages/platform-user-edit').then((m) => ({ default: m.PlatformUserEditPage })),
);
const AiAssistantsPage = lazy(() => import('@/modules/super-admin/pages/ai-assistants').then((m) => ({ default: m.AiAssistantsPage })));
const PricingDefinitionsPage = lazy(() =>
    import('@/modules/super-admin/pages/pricing-definitions').then((m) => ({ default: m.PricingDefinitionsPage })),
);
const SentimentCompetitorsAdminPage = lazy(() =>
    import('@/modules/super-admin/pages/sentiment-competitors-admin').then((m) => ({ default: m.SentimentCompetitorsAdminPage })),
);
const AdminAssistantSettingsPage = lazy(() =>
    import('@/modules/super-admin/pages/admin-assistant-settings').then((m) => ({ default: m.AdminAssistantSettingsPage })),
);
const PricingCompaniesPage = lazy(() =>
    import('@/modules/super-admin/pages/pricing-companies').then((m) => ({ default: m.PricingCompaniesPage })),
);
const PricingListingsPage = lazy(() =>
    import('@/modules/super-admin/pages/pricing-listings').then((m) => ({ default: m.PricingListingsPage })),
);
const PricingListingEditPage = lazy(() =>
    import('@/modules/super-admin/pages/pricing-listing-edit').then((m) => ({ default: m.PricingListingEditPage })),
);
const SubscriptionPlansPage = lazy(() =>
    import('@/modules/super-admin/pages/subscription-plans').then((m) => ({ default: m.SubscriptionPlansPage })),
);
const BillingOverviewPage = lazy(() =>
    import('@/modules/super-admin/pages/billing-overview').then((m) => ({ default: m.BillingOverviewPage })),
);
const TenantDashboardPage = lazy(() => import('@/modules/tenant/pages/dashboard').then((m) => ({ default: m.TenantDashboardPage })));
const TeamPage = lazy(() => import('@/modules/tenant/pages/team').then((m) => ({ default: m.TeamPage })));
const AddUserPage = lazy(() => import('@/modules/tenant/pages/add-user').then((m) => ({ default: m.AddUserPage })));
const EditUserPage = lazy(() => import('@/modules/tenant/pages/edit-user').then((m) => ({ default: m.EditUserPage })));
const SubscriptionPage = lazy(() => import('@/modules/tenant/pages/subscription').then((m) => ({ default: m.SubscriptionPage })));
const LeadsPage = lazy(() => import('@/modules/crm/pages/leads').then((m) => ({ default: m.LeadsPage })));
const LeadDetailPage = lazy(() => import('@/modules/crm/pages/lead-detail').then((m) => ({ default: m.LeadDetailPage })));
const AssistantOverviewPage = lazy(() =>
    import('@/modules/ai-assistant/pages/assistant-overview').then((m) => ({ default: m.AssistantOverviewPage })),
);
const AssistantSettingsPage = lazy(() =>
    import('@/modules/ai-assistant/pages/assistant-settings').then((m) => ({ default: m.AssistantSettingsPage })),
);
const ConversationsPage = lazy(() => import('@/modules/ai-assistant/pages/conversations').then((m) => ({ default: m.ConversationsPage })));
const ConversationDetailPage = lazy(() =>
    import('@/modules/ai-assistant/pages/conversation-detail').then((m) => ({ default: m.ConversationDetailPage })),
);
const LeadQualificationPage = lazy(() =>
    import('@/modules/ai-assistant/pages/lead-qualification').then((m) => ({ default: m.LeadQualificationPage })),
);
const AiSearchPage = lazy(() => import('@/modules/ai-assistant/pages/ai-search').then((m) => ({ default: m.AiSearchPage })));
const AiAnalyticsPage = lazy(() => import('@/modules/ai-assistant/pages/ai-analytics').then((m) => ({ default: m.AiAnalyticsPage })));
const PricingDashboardPage = lazy(() => import('@/modules/pricing/pages/dashboard').then((m) => ({ default: m.PricingDashboardPage })));
const AppDashboardPage = lazy(() => import('@/modules/pricing/pages/app-dashboard').then((m) => ({ default: m.AppDashboardPage })));
const MarketOverviewPage = lazy(() => import('@/modules/pricing/pages/market-overview').then((m) => ({ default: m.MarketOverviewPage })));
const SalesPerformancePage = lazy(() =>
    import('@/modules/pricing/pages/sales-performance').then((m) => ({ default: m.SalesPerformancePage })),
);
const GeoPricingPage = lazy(() => import('@/modules/pricing/pages/geo-pricing').then((m) => ({ default: m.GeoPricingPage })));
const AiPricingPage = lazy(() => import('@/modules/pricing/pages/ai-pricing').then((m) => ({ default: m.AiPricingPage })));
const GalleryPage = lazy(() => import('@/modules/pricing/pages/gallery').then((m) => ({ default: m.GalleryPage })));
const ShopRequestsPage = lazy(() => import('@/modules/pricing/pages/shop-requests').then((m) => ({ default: m.ShopRequestsPage })));
const HelpRequestsPage = lazy(() => import('@/modules/pricing/pages/help-requests').then((m) => ({ default: m.HelpRequestsPage })));
const FindCommunitiesPage = lazy(() =>
    import('@/modules/pricing/pages/find-communities').then((m) => ({ default: m.FindCommunitiesPage })),
);
const DataScrapingPage = lazy(() => import('@/modules/pricing/pages/data-scraping').then((m) => ({ default: m.DataScrapingPage })));
const ListingsPage = lazy(() => import('@/modules/pricing/pages/listings').then((m) => ({ default: m.ListingsPage })));
const PositionsPage = lazy(() => import('@/modules/pricing/pages/positions').then((m) => ({ default: m.PositionsPage })));
const AddCommunityPage = lazy(() => import('@/modules/pricing/pages/add-community').then((m) => ({ default: m.AddCommunityPage })));
const ListingDetailPage = lazy(() => import('@/modules/pricing/pages/listing-detail').then((m) => ({ default: m.ListingDetailPage })));
const HrmsOrganizationPage = lazy(() => import('@/modules/hrms/pages/organization').then((m) => ({ default: m.HrmsOrganizationPage })));
const HrmsEmployeesPage = lazy(() => import('@/modules/hrms/pages/employees').then((m) => ({ default: m.HrmsEmployeesPage })));
const HrmsEmployeeDetailPage = lazy(() => import('@/modules/hrms/pages/employee-detail').then((m) => ({ default: m.HrmsEmployeeDetailPage })));
const HrmsEmployeeFormPage = lazy(() => import('@/modules/hrms/pages/employee-form').then((m) => ({ default: m.HrmsEmployeeFormPage })));
const HrmsAttendancePage = lazy(() => import('@/modules/hrms/pages/attendance').then((m) => ({ default: m.HrmsAttendancePage })));
const HrmsLeavePage = lazy(() => import('@/modules/hrms/pages/leave').then((m) => ({ default: m.HrmsLeavePage })));
const HrmsCompensationPage = lazy(() => import('@/modules/hrms/pages/compensation').then((m) => ({ default: m.HrmsCompensationPage })));
const HrmsPayrollPage = lazy(() => import('@/modules/hrms/pages/payroll').then((m) => ({ default: m.HrmsPayrollPage })));
const HrmsTaxPage = lazy(() => import('@/modules/hrms/pages/tax').then((m) => ({ default: m.HrmsTaxPage })));
const HrmsLoansPage = lazy(() => import('@/modules/hrms/pages/loans').then((m) => ({ default: m.HrmsLoansPage })));
const HrmsSettlementPage = lazy(() => import('@/modules/hrms/pages/settlement').then((m) => ({ default: m.HrmsSettlementPage })));
const HrmsReportsPage = lazy(() => import('@/modules/hrms/pages/reports').then((m) => ({ default: m.HrmsReportsPage })));
const HrmsEssPage = lazy(() => import('@/modules/hrms/pages/ess').then((m) => ({ default: m.HrmsEssPage })));
const HrmsMssPage = lazy(() => import('@/modules/hrms/pages/mss').then((m) => ({ default: m.HrmsMssPage })));
const HrmsWorkflowsPage = lazy(() => import('@/modules/hrms/pages/workflows').then((m) => ({ default: m.HrmsWorkflowsPage })));
const HrmsApprovalsPage = lazy(() => import('@/modules/hrms/pages/approvals').then((m) => ({ default: m.HrmsApprovalsPage })));
const HrmsSettingsPage = lazy(() => import('@/modules/hrms/pages/settings').then((m) => ({ default: m.HrmsSettingsPage })));
const HrmsDashboardPage = lazy(() => import('@/modules/hrms/pages/dashboard').then((m) => ({ default: m.HrmsDashboardPage })));
const AppointmentsListPage = lazy(() =>
    import('@/modules/appointments/pages/appointments-list').then((m) => ({ default: m.AppointmentsListPage })),
);
const CalendarSettingsPage = lazy(() =>
    import('@/modules/appointments/pages/calendar-settings').then((m) => ({ default: m.CalendarSettingsPage })),
);
const CalendarCallbackPage = lazy(() =>
    import('@/modules/appointments/pages/calendar-callback').then((m) => ({ default: m.CalendarCallbackPage })),
);
const EmailSettingsPage = lazy(() =>
    import('@/modules/communications/pages/email-settings').then((m) => ({ default: m.EmailSettingsPage })),
);
const VoiceSettingsPage = lazy(() =>
    import('@/modules/communications/pages/voice-settings').then((m) => ({ default: m.VoiceSettingsPage })),
);
const WhatsappSettingsPage = lazy(() =>
    import('@/modules/communications/pages/whatsapp-settings').then((m) => ({ default: m.WhatsappSettingsPage })),
);
const SmsSettingsPage = lazy(() => import('@/modules/communications/pages/sms-settings').then((m) => ({ default: m.SmsSettingsPage })));
const RolesPermissionsPage = lazy(() =>
    import('@/modules/rbac/pages/roles-permissions').then((m) => ({ default: m.RolesPermissionsPage })),
);
const SettingsPage = lazy(() => import('@/modules/tenant/pages/settings').then((m) => ({ default: m.SettingsPage })));
const ProfilePage = lazy(() => import('@/modules/auth/pages/profile').then((m) => ({ default: m.ProfilePage })));
const ChangePasswordPage = lazy(() => import('@/modules/auth/pages/change-password').then((m) => ({ default: m.ChangePasswordPage })));
const PricingSettingsPage = lazy(() =>
    import('@/modules/pricing/pages/pricing-settings').then((m) => ({ default: m.PricingSettingsPage })),
);
const PricingRulesPage = lazy(() => import('@/modules/pricing/pages/pricing-rules').then((m) => ({ default: m.PricingRulesPage })));
const RecommendationsPage = lazy(() => import('@/modules/pricing/pages/recommendations').then((m) => ({ default: m.RecommendationsPage })));
const PricingReportsPage = lazy(() => import('@/modules/pricing/pages/pricing-reports').then((m) => ({ default: m.PricingReportsPage })));

const SentimentDashboardPage = lazy(() =>
    import('@/modules/sentiment-analysis/pages/dashboard').then((m) => ({ default: m.SentimentDashboardPage })),
);
const SentimentInsightsPage = lazy(() => import('@/modules/sentiment-analysis/pages/insights').then((m) => ({ default: m.InsightsPage })));
const SentimentReviewsPage = lazy(() => import('@/modules/sentiment-analysis/pages/reviews').then((m) => ({ default: m.ReviewsPage })));
const SentimentReviewInboxPage = lazy(() =>
    import('@/modules/sentiment-analysis/pages/review-inbox').then((m) => ({ default: m.ReviewInboxPage })),
);
const SentimentTrendsPage = lazy(() =>
    import('@/modules/sentiment-analysis/pages/sentiment-trends').then((m) => ({ default: m.SentimentTrendsPage })),
);
const SentimentRatingsPage = lazy(() => import('@/modules/sentiment-analysis/pages/ratings').then((m) => ({ default: m.RatingsPage })));
const SentimentReputationScorePage = lazy(() =>
    import('@/modules/sentiment-analysis/pages/reputation-score').then((m) => ({ default: m.ReputationScorePage })),
);
const SentimentTopicsPage = lazy(() => import('@/modules/sentiment-analysis/pages/topics').then((m) => ({ default: m.TopicsPage })));
const SentimentKeywordsPage = lazy(() => import('@/modules/sentiment-analysis/pages/keywords').then((m) => ({ default: m.KeywordsPage })));
const SentimentLocationsPage = lazy(() =>
    import('@/modules/sentiment-analysis/pages/locations').then((m) => ({ default: m.LocationsPage })),
);
const SentimentSourcesPage = lazy(() => import('@/modules/sentiment-analysis/pages/sources').then((m) => ({ default: m.SourcesPage })));
const SentimentCompetitorsPage = lazy(() =>
    import('@/modules/sentiment-analysis/pages/competitors').then((m) => ({ default: m.CompetitorsPage })),
);
const SentimentComparisonPage = lazy(() =>
    import('@/modules/sentiment-analysis/pages/comparison').then((m) => ({ default: m.ComparisonPage })),
);
const SentimentAlertsPage = lazy(() => import('@/modules/sentiment-analysis/pages/alerts').then((m) => ({ default: m.AlertsPage })));
const SentimentReportsPage = lazy(() => import('@/modules/sentiment-analysis/pages/reports').then((m) => ({ default: m.ReportsPage })));
const SentimentSettingsPage = lazy(() =>
    import('@/modules/sentiment-analysis/pages/settings').then((m) => ({ default: m.SentimentSettingsPage })),
);

const AnalyticsPage = lazy(() => import('@/modules/workspace/pages/analytics').then((m) => ({ default: m.AnalyticsPage })));
const WorkspaceReportsPage = lazy(() => import('@/modules/workspace/pages/reports').then((m) => ({ default: m.ReportsPage })));

function RouteFallback() {
    return (
        <div className="flex h-full min-h-[50vh] items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Loading…
        </div>
    );
}

/**
 * The backend's `platform_admin` middleware already rejects these API calls for
 * a tenant user, but without this guard the page shell would still render
 * (just full of empty states/errors) for anyone who types the URL directly —
 * this sends them back to their own dashboard instead.
 */
function RequirePlatformAdmin() {
    const { user } = useAuth();
    return user?.is_platform_admin ? <Outlet /> : <Navigate to="/dashboard" replace />;
}

/**
 * Backend endpoints for roles/permissions already 403 for anyone who isn't a
 * tenant Admin (see RoleController::requireTenantAdmin) — this guard just
 * keeps a non-admin Member from landing on an empty/erroring page shell if
 * they type the URL directly.
 */
function RequireTenantAdmin() {
    const { user } = useAuth();
    return user?.is_platform_admin || user?.is_tenant_admin ? <Outlet /> : <Navigate to="/dashboard" replace />;
}

function collectTopLevelSegments(items: NavItem[]): Set<string> {
    const segments = new Set<string>();

    function walk(list: NavItem[]) {
        for (const item of list) {
            if (item.href) {
                const first = item.href.split('/').filter(Boolean)[0];
                if (first) segments.add(`/${first}`);
            }
            if (item.children) walk(item.children);
        }
    }

    walk(items);
    return segments;
}

/**
 * The backend's `module:` middleware (EnsureModuleEnabled) already 403s API
 * calls for a module the tenant/user doesn't have — but without this guard
 * the page shell still mounted for anyone who typed the URL directly, just
 * silently showing empty data (GET failures don't toast — see api.ts). The
 * sidebar already hides disabled modules via the same nav-filtering
 * (NavigationBuilder → ModuleGate), so reusing that same /nav response here
 * keeps "what's visible" and "what's reachable" from ever disagreeing.
 */
function RequireModule() {
    const { user } = useAuth();
    const location = useLocation();
    const { data: nav, isLoading } = useNav();

    if (user?.is_platform_admin) {
        return <Outlet />;
    }

    if (isLoading || !nav) {
        return <RouteFallback />;
    }

    const segment = `/${location.pathname.split('/').filter(Boolean)[0] ?? ''}`;
    const allowed = collectTopLevelSegments(nav);

    if (allowed.has(segment)) {
        return <Outlet />;
    }

    return (
        <div className="space-y-6">
            <EmptyState
                icon={Lock}
                title="Access restricted"
                description="This application isn't enabled for your account. Ask your workspace admin to assign it to you under Users."
            />
        </div>
    );
}

export function App() {
    useAuthBootstrap();

    return (
        <QueryClientProvider client={queryClient}>
            <TooltipProvider>
                <BrowserRouter>
                    <Suspense fallback={<RouteFallback />}>
                        <Routes>
                            <Route path="/" element={<LandingPage />} />
                            <Route path="/why-aipa" element={<WhyAipaPage />} />
                            <Route path="/resources/help" element={<HelpCenterPage />} />
                            <Route path="/company/about" element={<AboutPage />} />
                            <Route path="/company/contact" element={<ContactPage />} />
                            <Route path="/company/book-a-demo" element={<BookADemoPage />} />
                            <Route path="/industries/:slug" element={<IndustryDetailPage />} />
                            <Route path="/products/:slug" element={<ProductDetailPage />} />
                            <Route path="/platform/:slug" element={<PlatformDetailPage />} />
                            <Route path="/legal/terms" element={<TermsPage />} />
                            <Route path="/legal/privacy" element={<PrivacyPage />} />

                            <Route element={<AuthLayout />}>
                                <Route path="/login" element={<LoginPage />} />
                            </Route>

                            <Route element={<AppLayout />}>
                                <Route path="/dashboard" element={<TenantDashboardPage />} />
                                <Route path="/users" element={<TeamPage />} />
                                <Route path="/users/new" element={<AddUserPage />} />
                                <Route path="/users/:id/edit" element={<EditUserPage />} />
                                <Route element={<RequireTenantAdmin />}>
                                    <Route path="/roles" element={<RolesPermissionsPage initialTab="roles" />} />
                                    <Route path="/permissions" element={<RolesPermissionsPage initialTab="permissions" />} />
                                    <Route path="/billing" element={<Navigate to="/subscription" replace />} />
                                    <Route path="/subscription" element={<SubscriptionPage />} />
                                </Route>
                                <Route path="/settings" element={<SettingsPage />} />
                                <Route path="/profile" element={<ProfilePage />} />
                                <Route path="/change-password" element={<ChangePasswordPage />} />
                                <Route path="/integrations" element={<Navigate to="/settings?tab=integrations" replace />} />
                                <Route path="/automation" element={<Navigate to="/settings?tab=automation" replace />} />
                                <Route path="/ai_configuration" element={<Navigate to="/settings?tab=ai_configuration" replace />} />
                                <Route path="/reports" element={<WorkspaceReportsPage />} />
                                <Route path="/analytics" element={<AnalyticsPage />} />

                                {/*
                                    Email/Voice/WhatsApp/SMS are deliberately reachable outside
                                    RequireModule: they're "hidden" modules (see
                                    NavigationBuilder.$adminOnlyModuleKeys) that never appear in
                                    the tenant's nav, so RequireModule's nav-derived segment check
                                    would block every tenant user from ever reaching them — even
                                    once Super Admin turns on Allow Configuration for a channel.
                                    Each page gates its own editability against
                                    tenant.channel_permissions / is_tenant_admin instead, the same
                                    way /integrations and /settings (which link to these routes)
                                    are themselves reachable without a module check.
                                */}
                                <Route path="/email" element={<EmailSettingsPage />} />
                                <Route path="/voice" element={<VoiceSettingsPage />} />
                                <Route path="/whatsapp" element={<WhatsappSettingsPage />} />
                                <Route path="/sms" element={<SmsSettingsPage />} />

                                <Route element={<RequireModule />}>
                                    <Route path="/leads" element={<LeadsPage />} />
                                    <Route path="/leads/:id" element={<LeadDetailPage />} />
                                    <Route path="/crm" element={<Navigate to="/leads" replace />} />
                                    <Route path="/ai_assistant" element={<AssistantOverviewPage />} />
                                    <Route path="/ai_assistant/assistant_builder" element={<Navigate to="/ai_assistant" replace />} />
                                    <Route path="/ai_assistant/knowledge_base" element={<Navigate to="/ai_assistant" replace />} />
                                    <Route path="/ai_assistant/conversations" element={<ConversationsPage />} />
                                    <Route path="/ai_assistant/conversations/:id" element={<ConversationDetailPage />} />
                                    <Route path="/ai_assistant/lead_qualification" element={<LeadQualificationPage />} />
                                    <Route path="/ai_assistant/ai_search" element={<AiSearchPage />} />
                                    <Route path="/ai_assistant/ai_analytics" element={<AiAnalyticsPage />} />
                                    <Route path="/ai_assistant/:id/settings" element={<AssistantSettingsPage />} />
                                    <Route
                                        path="/pricing_assistant"
                                        element={<Navigate to="/pricing_assistant/pricing_dashboard" replace />}
                                    />
                                    <Route path="/pricing_assistant/pricing_dashboard" element={<PricingDashboardPage />} />
                                    <Route path="/pricing_assistant/app_dashboard" element={<AppDashboardPage />} />
                                    <Route path="/pricing_assistant/market_overview" element={<MarketOverviewPage />} />
                                    <Route path="/pricing_assistant/sales_performance" element={<SalesPerformancePage />} />
                                    <Route path="/pricing_assistant/geo_pricing" element={<GeoPricingPage />} />
                                    <Route path="/pricing_assistant/ai_pricing" element={<AiPricingPage />} />
                                    <Route path="/pricing_assistant/find_communities" element={<FindCommunitiesPage />} />
                                    <Route path="/pricing_assistant/pricing_intelligence" element={<ListingsPage />} />
                                    <Route path="/pricing_assistant/communities/new" element={<AddCommunityPage />} />
                                    <Route path="/pricing_assistant/positions" element={<PositionsPage />} />
                                    <Route path="/pricing_assistant/gallery" element={<GalleryPage />} />
                                    <Route path="/pricing_assistant/shop_requests" element={<ShopRequestsPage />} />
                                    <Route path="/pricing_assistant/help_requests" element={<HelpRequestsPage />} />
                                    <Route path="/pricing_assistant/pricing_rules" element={<PricingRulesPage />} />
                                    <Route path="/pricing_assistant/pricing_data" element={<DataScrapingPage />} />
                                    <Route path="/pricing_assistant/recommendations" element={<RecommendationsPage />} />
                                    <Route path="/pricing_assistant/pricing_reports" element={<PricingReportsPage />} />
                                    <Route path="/pricing_assistant/settings" element={<PricingSettingsPage />} />
                                    <Route path="/pricing_assistant/:id" element={<ListingDetailPage />} />
                                    <Route path="/appointments" element={<AppointmentsListPage />} />
                                    <Route path="/appointments/calendar" element={<CalendarSettingsPage />} />
                                    <Route path="/oauth/calendar/:provider/callback" element={<CalendarCallbackPage />} />

                                    <Route path="/sentiment_analysis" element={<Navigate to="/sentiment_analysis/dashboard" replace />} />
                                    <Route path="/sentiment_analysis/dashboard" element={<SentimentDashboardPage />} />
                                    <Route path="/sentiment_analysis/insights" element={<SentimentInsightsPage />} />
                                    <Route path="/sentiment_analysis/reviews" element={<SentimentReviewsPage />} />
                                    <Route path="/sentiment_analysis/review_inbox" element={<SentimentReviewInboxPage />} />
                                    <Route path="/sentiment_analysis/sentiment_trends" element={<SentimentTrendsPage />} />
                                    <Route path="/sentiment_analysis/ratings" element={<SentimentRatingsPage />} />
                                    <Route path="/sentiment_analysis/reputation_score" element={<SentimentReputationScorePage />} />
                                    <Route path="/sentiment_analysis/topics" element={<SentimentTopicsPage />} />
                                    <Route path="/sentiment_analysis/keywords" element={<SentimentKeywordsPage />} />
                                    <Route path="/sentiment_analysis/locations" element={<SentimentLocationsPage />} />
                                    <Route path="/sentiment_analysis/sources" element={<SentimentSourcesPage />} />
                                    <Route path="/sentiment_analysis/competitors" element={<SentimentCompetitorsPage />} />
                                    <Route path="/sentiment_analysis/comparison" element={<SentimentComparisonPage />} />
                                    <Route path="/sentiment_analysis/alerts" element={<SentimentAlertsPage />} />
                                    <Route path="/sentiment_analysis/reports" element={<SentimentReportsPage />} />
                                    <Route path="/sentiment_analysis/settings" element={<SentimentSettingsPage />} />

                                    <Route path="/hrms" element={<HrmsDashboardPage />} />
                                    <Route path="/hrms/organization" element={<HrmsOrganizationPage />} />
                                    <Route path="/hrms/employees" element={<HrmsEmployeesPage />} />
                                    <Route path="/hrms/employees/new" element={<HrmsEmployeeFormPage />} />
                                    <Route path="/hrms/employees/:id" element={<HrmsEmployeeDetailPage />} />
                                    <Route path="/hrms/employees/:id/edit" element={<HrmsEmployeeFormPage />} />
                                    <Route path="/hrms/attendance" element={<HrmsAttendancePage />} />
                                    <Route path="/hrms/leave" element={<HrmsLeavePage />} />
                                    <Route path="/hrms/compensation" element={<HrmsCompensationPage />} />
                                    <Route path="/hrms/payroll" element={<HrmsPayrollPage />} />
                                    <Route path="/hrms/tax" element={<HrmsTaxPage />} />
                                    <Route path="/hrms/loans" element={<HrmsLoansPage />} />
                                    <Route path="/hrms/settlement" element={<HrmsSettlementPage />} />
                                    <Route path="/hrms/reports" element={<HrmsReportsPage />} />
                                    <Route path="/hrms/ess" element={<HrmsEssPage />} />
                                    <Route path="/hrms/mss" element={<HrmsMssPage />} />
                                    <Route path="/hrms/workflows" element={<HrmsWorkflowsPage />} />
                                    <Route path="/hrms/approvals" element={<HrmsApprovalsPage />} />
                                    <Route path="/hrms/settings" element={<HrmsSettingsPage />} />
                                </Route>

                                <Route element={<RequirePlatformAdmin />}>
                                    <Route path="/admin" element={<SuperAdminDashboardPage />} />
                                    <Route path="/admin/tenants" element={<SuperAdminTenantsPage />} />
                                    <Route path="/admin/tenants/new" element={<NewTenantPage />} />
                                    <Route path="/admin/tenants/:id/modules" element={<TenantModulesPage />} />
                                    <Route path="/admin/tenants/:id/edit" element={<EditTenantPage />} />
                                    <Route path="/admin/industries" element={<IndustriesPage />} />
                                    <Route path="/admin/industries/:id" element={<IndustryTemplatePage />} />
                                    <Route path="/admin/users" element={<PlatformUsersPage />} />
                                    <Route path="/admin/users/new" element={<PlatformUserAddPage />} />
                                    <Route path="/admin/users/:id/edit" element={<PlatformUserEditPage />} />
                                    <Route path="/admin/ai-assistants" element={<AiAssistantsPage />} />
                                    <Route path="/admin/ai-assistants/:id/settings" element={<AdminAssistantSettingsPage />} />
                                    <Route path="/admin/pricing-definitions" element={<PricingDefinitionsPage />} />
                                    <Route path="/admin/pricing-definitions/*" element={<Navigate to="/admin/pricing-definitions" replace />} />
                                    <Route path="/admin/sentiment-competitors" element={<SentimentCompetitorsAdminPage />} />
                                    <Route path="/admin/pricing-companies" element={<PricingCompaniesPage />} />
                                    <Route path="/admin/pricing-listings" element={<PricingListingsPage />} />
                                    <Route path="/admin/pricing-listings/:id/edit" element={<PricingListingEditPage />} />
                                    <Route path="/admin/subscriptions" element={<SubscriptionPlansPage />} />
                                    <Route path="/admin/payment-methods" element={<Navigate to="/admin/settings" replace />} />
                                    <Route path="/admin/billing" element={<BillingOverviewPage />} />
                                    <Route path="/admin/modules" element={<ModulesPage />} />
                                    <Route path="/admin/roles" element={<PlatformRolesPage />} />
                                    <Route path="/admin/ai/providers" element={<Navigate to="/admin/settings" replace />} />
                                    <Route path="/admin/mail-settings" element={<Navigate to="/admin/settings" replace />} />
                                    <Route path="/admin/reports" element={<ReportsPage />} />
                                    <Route path="/admin/analytics" element={<PlatformAnalyticsPage />} />
                                    <Route path="/admin/audit-logs" element={<AuditLogsPage />} />
                                    <Route path="/admin/settings" element={<SystemSettingsPage />} />
                                    <Route path="/admin/consent-settings" element={<Navigate to="/admin/settings" replace />} />
                                    <Route path="/admin/demo-requests" element={<DemoRequestsPage />} />
                                    <Route path="/admin/hr-employees" element={<HrEmployeesPage />} />
                                    <Route path="/admin/contact-messages" element={<ContactMessagesPage />} />
                                </Route>
                            </Route>

                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                    </Suspense>
                </BrowserRouter>
                <Toaster />
                <ConsentPreferencesLauncher />
            </TooltipProvider>
        </QueryClientProvider>
    );
}
